import { CommonModule, DatePipe } from '@angular/common';
import {
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';

import {
  FormsModule,
} from '@angular/forms';

import {
  Subject,
  takeUntil,
} from 'rxjs';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import Swal from 'sweetalert2';

import {
  AttendanceApiError,
  AttendanceCredentialsPayload,
  AttendanceEmployee,
  AttendanceNextAction,
  AttendanceRecord,
} from 'src/app/core/models/attendance';

import {
  AttendanceService,
} from 'src/app/core/services/attendance.service';

@Component({
  selector: 'app-markattendance',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
  ],
  providers: [
    DatePipe,
  ],
  templateUrl:
    './markattendance.component.html',
  styleUrl:
    './markattendance.component.scss',
})
export class MarkattendanceComponent implements OnInit, OnDestroy {

  private readonly destroy$ = new Subject<void>();
  private verificationModal!: NgbModalRef;

  /* =====================================================
      COMPANY
  ===================================================== */

  readonly companyName = 'Brainex World (Pvt.) Ltd.';
  readonly currentYear = new Date().getFullYear();

  /* =====================================================
      DATE & TIME
  ===================================================== */

  currentDate = new Date();
  currentTime = new Date();

  private clockInterval: ReturnType<typeof setInterval> | null = null;

  /* =====================================================
      PIN
  ===================================================== */

  attendancePin = '';

  readonly maximumPinLength = 4;

  showAttendancePin = false;

  /* =====================================================
      VERIFY
  ===================================================== */

  isVerifying = false;

  isVerified = false;

  nextAction:
    AttendanceNextAction | null =
    null;

  verifiedEmployee:
    AttendanceEmployee | null =
    null;

  verifiedAttendance:
    AttendanceRecord | null =
    null;

  /* =====================================================
      SUBMIT
  ===================================================== */

  isSubmitting = false;

  isAttendanceCompleted = false;

  attendanceResult:
    AttendanceRecord | null =
    null;

  attendanceMessage = '';
  attendanceError = '';
  showVerificationPopup = false;

  showSuccessPopup = false;

  /* =====================================================
      CONSTRUCTOR
  ===================================================== */

  constructor(
    private modalService: NgbModal,
    private readonly attendanceService:
      AttendanceService,

    private readonly datePipe:
      DatePipe
  ) { }

  /* =====================================================
      INIT
  ===================================================== */

  ngOnInit(): void {

    this.startLiveClock();

  }

  /* =====================================================
      LIVE CLOCK
  ===================================================== */

  private startLiveClock(): void {

    this.currentDate =
      new Date();

    this.currentTime =
      new Date();

    this.clockInterval =
      setInterval(() => {

        this.currentTime =
          new Date();

      }, 1000);

  }

  /* =====================================================
      PIN INPUT
  ===================================================== */

  onAttendancePinInput(
    event: Event
  ): void {

    const input =
      event.target as HTMLInputElement;

    const value =
      input.value
        .replace(/\D/g, '')
        .slice(0, 4);

    input.value =
      value;

    this.attendancePin =
      value;

    if (this.isVerified) {

      this.resetVerification(false);

    }

  }

  /* =====================================================
      SHOW / HIDE PIN
  ===================================================== */

  toggleAttendancePin(): void {

    this.showAttendancePin =
      !this.showAttendancePin;

  }

  /* =====================================================
      VERIFY PIN
  ===================================================== */

  verifyAttendance(): void {

    this.attendanceError = '';

    if (!this.isValidAttendancePin) {

      this.showWarning(

        'Invalid Attendance PIN',

        'Please enter your 4-digit attendance PIN.'

      );

      return;

    }

    const payload: AttendanceCredentialsPayload = {

      attendancePin: this.attendancePin,

    };

    this.isVerifying = true;

    this.resetVerification(false);

    this.attendanceService
      .verifyAttendance(payload)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe({

        next: (response) => {

          this.isVerifying = false;

          // 👇 Attendance already completed
          if (response.nextAction === 'completed') {

            Swal.fire({
              icon: 'info',
              title: 'Attendance Already Completed',
              text: response.message,
              confirmButtonText: 'OK',
              confirmButtonColor: '#4b0082'
            });

            return;
          }

          this.isVerified = true;
          this.nextAction = response.nextAction;
          this.verifiedEmployee = response.data.employee;
          this.verifiedAttendance = response.data.attendance;

          this.showVerificationPopup = true;

        },

        error: (error) => {

          this.isVerifying = false;

          this.handleApiError(

            error,

            'Verification Failed'

          );

        },

      });

  }

  /* =====================================================
    MARK ATTENDANCE
  ===================================================== */

  markAttendance(): void {

    if (

      !this.nextAction ||

      this.nextAction === 'completed' ||

      !this.isValidAttendancePin

    ) {

      return;

    }

    const payload: AttendanceCredentialsPayload = {

      attendancePin: this.attendancePin,

    };

    this.isSubmitting = true;

    this.attendanceError = '';

    const attendanceRequest =

      this.nextAction === 'check-in'

        ? this.attendanceService.checkIn(payload)

        : this.attendanceService.checkOut(payload);

    attendanceRequest
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe({

        next: (response) => {

          this.isSubmitting = false;

          // Close Verification Popup
          this.showVerificationPopup = false;

          this.isAttendanceCompleted = true;

          this.verifiedEmployee =
            response.data.employee;

          this.attendanceResult =
            response.data.attendance;

          this.attendanceMessage =
            response.message;

          this.nextAction =
            response.data.attendance.attendanceState ===
              'checked-out'

              ? 'completed'

              : 'check-out';

        },

        error: (error) => {

          this.isSubmitting = false;

          this.handleApiError(

            error,

            this.nextAction === 'check-in'

              ? 'Check-In Failed'

              : 'Check-Out Failed'

          );

        },

      });

  }

  /* =====================================================
      RESET SCREEN
  ===================================================== */

  markAnotherAttendance(): void {
    this.attendancePin = '';
    this.showAttendancePin = false;
    this.isAttendanceCompleted = false;
    this.attendanceResult = null;
    this.attendanceMessage = '';
    this.attendanceError = '';
    this.resetVerification();
  }

  /* =====================================================
      RESET VERIFY STATE
  ===================================================== */

  private resetVerification(

    resetCompleted: boolean = true

  ): void {

    this.nextAction = null;
    this.isVerified = false;
    this.verifiedEmployee = null;
    this.verifiedAttendance = null;

    if (resetCompleted) {
      this.isAttendanceCompleted = false;
      this.attendanceResult = null;
      this.attendanceMessage = '';
    }

  }

  /* =====================================================
      API ERROR
  ===================================================== */

  private handleApiError(

    error: any,

    title: string

  ): void {

    const apiError: AttendanceApiError =

      error?.error;

    let message =

      apiError?.message ||

      'Something went wrong. Please try again.';

    if (apiError?.errors?.length) {

      message = apiError.errors

        .map(error => error.message)

        .join('\n');

    }

    this.attendanceError = message;

    Swal.fire({

      icon: 'error',

      title,

      text: message,

      confirmButtonText: 'OK',

      confirmButtonColor: '#4b0082',

      customClass: {

        popup:
          'brainex-attendance-alert',

      },

    });

  }

  /* =====================================================
      WARNING
  ===================================================== */

  private showWarning(

    title: string,

    message: string

  ): void {

    Swal.fire({

      icon: 'warning',

      title,

      text: message,

      confirmButtonText: 'OK',

      confirmButtonColor: '#4b0082',

    });

  }

  /* =====================================================
    ENTER KEY SHORTCUT
===================================================== */

  handleEnterKey(): void {

    if (

      this.isAttendanceCompleted ||

      this.isSubmitting ||

      this.isVerifying

    ) {

      return;

    }

    if (!this.isVerified) {

      this.verifyAttendance();

      return;

    }

    if (this.nextAction !== 'completed') {

      this.markAttendance();

    }

  }

  /* =====================================================
      VALIDATION
  ===================================================== */

  get isValidAttendancePin(): boolean {

    return /^[0-9]{4}$/.test(

      this.attendancePin

    );

  }

  get canVerifyAttendance(): boolean {

    return (

      this.isValidAttendancePin &&

      !this.isVerifying &&

      !this.isSubmitting

    );

  }

  get canMarkAttendance(): boolean {

    return (

      this.isVerified &&

      this.nextAction !== null &&

      this.nextAction !== 'completed' &&

      !this.isSubmitting

    );

  }

  /* =====================================================
      ACTION UI
  ===================================================== */

  get actionTitle(): string {

    switch (this.nextAction) {

      case 'check-in':
        return 'Ready for Check-In';

      case 'check-out':
        return 'Ready for Check-Out';

      case 'completed':
        return 'Attendance Completed';

      default:
        return 'Attendance';

    }

  }

  get actionDescription(): string {

    switch (this.nextAction) {

      case 'check-in':

        return 'Your identity has been verified. Press the button below to record your arrival.';

      case 'check-out':

        return 'Your identity has been verified. Press the button below to record your departure.';

      case 'completed':

        return 'Your attendance for today has already been completed.';

      default:

        return '';

    }

  }

  get actionButtonText(): string {

    switch (this.nextAction) {

      case 'check-in':
        return 'Confirm Check-In';

      case 'check-out':
        return 'Confirm Check-Out';

      default:
        return 'Completed';

    }

  }

  get actionIcon(): string {

    switch (this.nextAction) {

      case 'check-in':
        return 'ri-login-box-line';

      case 'check-out':
        return 'ri-logout-box-r-line';

      default:
        return 'ri-checkbox-circle-line';

    }

  }

  /* =====================================================
      EMPLOYEE INITIALS
  ===================================================== */

  get employeeInitials(): string {

    const fullName =

      this.verifiedEmployee?.fullName ||

      'BW';

    return fullName
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map(name => name.charAt(0))
      .join('')
      .toUpperCase();

  }

  /* =====================================================
      DATE & TIME FORMATTERS
  ===================================================== */

  get formattedCurrentDate(): string {

    return (

      this.datePipe.transform(

        this.currentDate,

        'EEEE, dd MMMM yyyy'

      ) || ''

    );

  }

  get formattedCurrentTime(): string {

    return (

      this.datePipe.transform(

        this.currentTime,

        'hh:mm:ss a'

      ) || ''

    );

  }

  get formattedCheckInTime(): string {

    return (

      this.datePipe.transform(

        this.attendanceResult?.checkInTime,

        'hh:mm a'

      ) || '--:--'

    );

  }

  get formattedCheckOutTime(): string {

    return (
      this.datePipe.transform(
        this.attendanceResult?.checkOutTime,

        'hh:mm a'

      ) || '--:--'

    );

  }

  get formattedWorkingDuration(): string {

    const totalMinutes = this.attendanceResult?.totalWorkingMinutes || 0;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours === 0) {
      return `${minutes} min`;
    }

    return `${hours} hr ${minutes} min`;

  }

  /* =====================================================
      DESTROY
  ===================================================== */

  ngOnDestroy(): void {
    if (this.clockInterval) {
      clearInterval(this.clockInterval);
    }

    this.destroy$.next();
    this.destroy$.complete();
  }



  /* =====================================================
      Attendance Summary
  ===================================================== */
  get isCheckIn(): boolean {

    return this.attendanceResult?.attendanceState === 'checked-in';

  }

  get greeting(): string {

    const hour = new Date().getHours();

    if (hour < 12) {

      return 'Good Morning';

    }

    if (hour < 17) {

      return 'Good Afternoon';

    }

    return 'Good Evening';

  }

  get summaryTitle(): string {

    return this.isCheckIn

      ? 'Welcome Back!'

      : 'Have a Great Evening!';

  }

  get summaryMessage(): string {

    return this.isCheckIn

      ? 'Your Check-In has been successfully recorded. We wish you a productive, successful and enjoyable working day.'

      : 'Your Check-Out has been successfully recorded. Thank you for your dedication and hard work today. Have a safe journey home and enjoy your evening.';

  }
}